import { Link } from 'react-router-dom';
import { themeClasses } from '@member-portal/ui';
import {
  SparklesIcon,
  UserIcon,
  StarIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  MapIcon,
} from '@heroicons/react/24/outline';

const FH_FEATURES_SAMPLE_URL =
  'https://www.fourhundred.com/july-2026/in-conversation-with-amy-finsilver';
const ADVERTORIAL_SAMPLE_URL =
  'https://www.fourhundred.com/society-archive/an-italian-journey-with-rocco-forte-hotels?j=371225&sfmc_sub=188708732&l=8439_HTML&u=7502496&mid=526001300&jb=0';

const sampleCtaClassName = `inline-block mt-8 px-6 py-3 border border-theme-border ${themeClasses.text.button} text-theme-text hover:bg-theme-bg-secondary transition-colors duration-300 uppercase`;

const valuePropositions = [
  {
    icon: SparklesIcon,
    title: 'Elevated Storytelling',
    description:
      'Communicate news and introduce new product through a story-led strategy in beautifully designed e-publications',
  },
  {
    icon: UserIcon,
    title: 'Targeted Reach',
    description:
      'Access an audience of travel advisors and an influential membership-based community of affluent consumers',
  },
  {
    icon: StarIcon,
    title: 'New Audience Acquisition',
    description: 'Drive brand awareness among a highly selective, proprietary audience',
  },
];

const advisorNetwork = [
  {
    icon: MapIcon,
    title: 'The Four Hundred Travel Team',
    description: 'In-house travel advisors servicing an affluent membership-based community.',
  },
  {
    icon: DocumentTextIcon,
    title: 'The Four Hundred Advisor Network',
    description: 'Hosted travel advisors focused on luxury travel planning and experiences.',
  },
  {
    icon: GlobeAltIcon,
    title: 'The Independent Advisor Community',
    description:
      'Non-hosted travel advisors, agency owners and managers seeking to provide white glove travel service; a majority of this audience are unaffiliated with any consortia.',
  },
];

const ageDistribution = [
  { generation: 'Millennials', percentage: '40%', width: '100%' },
  { generation: 'Gen Z', percentage: '25%', width: '62.5%' },
  { generation: 'Gen X', percentage: '20%', width: '50%' },
  { generation: 'Boomers', percentage: '14%', width: '35%' },
  { generation: 'Gen Alpha', percentage: '1%', width: '2.5%' },
];

function PartnerOpportunitiesSections() {
  return (
    <>
      <section className="py-20 bg-theme-bg">
        <div className={themeClasses.container}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className={`${themeClasses.title.h2}`}>
                ABOUT FOUR HUNDRED BY DESIGN (FH<span className="lowercase">x</span>D)
              </h2>
            </div>
            <div className="space-y-6">
              <p className={`${themeClasses.text.body} text-lg leading-relaxed`}>
                Four Hundred by Design (FHxD) connects a highly targeted travel advisor and consumer
                audience to a curated collection of world-class hotels, resorts, villas, private
                residences, specialty cruise lines and providers of luxury goods and services through
                strategic exposure and high-impact marketing.
              </p>
              <p className={`${themeClasses.text.muted} text-base leading-relaxed`}>
                FHxD is the marketing division of Four Hundred, a private, membership-based community
                of innovators, visionaries, and industry leaders.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-theme-bg">
        <div className={themeClasses.container}>
          <div className="w-full h-1 bg-black" />
        </div>
      </div>

      <section className="py-20 bg-theme-bg">
        <div className={themeClasses.container}>
          <h2 className={`${themeClasses.title.h2} text-center mb-16`}>Why Partner With FHxD?</h2>
          <div className={themeClasses.grid.cols3}>
            {valuePropositions.map((prop, index) => (
              <div key={index} className="p-8 border border-theme-border bg-theme-bg-secondary">
                <prop.icon className="h-10 w-10 mb-6 text-theme-text-muted" strokeWidth={1} />
                <h3 className={`${themeClasses.title.h5} mb-4`}>{prop.title}</h3>
                <p className={`${themeClasses.text.muted}`}>{prop.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pt-20 pb-8 bg-theme-bg-secondary">
        <div className={themeClasses.container}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img src="/images/images/INSIDER.jpg" alt="INSIDER Magazine" className="w-full" />
            </div>
            <div>
              <h3 className={`${themeClasses.title.h3} mb-6`}>Insider E-Magazine</h3>
              <p className={`${themeClasses.text.body} text-lg leading-relaxed`}>
                INSIDER is a themed e-magazine featuring insider knowledge and unique customer
                experiences via editorial and advertising opportunities.
              </p>
              <Link to="/insider" className={sampleCtaClassName}>
                View a Sample
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-theme-bg-secondary">
        <div className={themeClasses.container}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className={`${themeClasses.title.h3} mb-6`}>FH Features E-Newsletter</h3>
              <p className={`${themeClasses.text.body} text-lg leading-relaxed`}>
                FH Features is an e-newsletter designed to promote exciting news such as new product
                launches and unique offerings. Culinary, experience, exploration and lifestyle-focused
                news strategically drive travel advisor and consumer engagement and awareness.
              </p>
              <a
                href={FH_FEATURES_SAMPLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={sampleCtaClassName}
              >
                View a Sample
              </a>
            </div>
            <div>
              <img
                src="/images/images/Hotel-Spotlight-Feature.jpg"
                alt="FH Features E-Newsletter"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pt-8 pb-20 bg-theme-bg-secondary">
        <div className={themeClasses.container}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img src="/images/images/FH-Feature.jpg" alt="Advertorial Feature" className="w-full" />
            </div>
            <div>
              <h3 className={`${themeClasses.title.h3} mb-6`}>Advertorial Feature</h3>
              <p className={`${themeClasses.text.body} text-lg leading-relaxed`}>
                Promote culinary and luxury lifestyle offerings, amazing experiences and destination
                and area activities.
              </p>
              <a
                href={ADVERTORIAL_SAMPLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={sampleCtaClassName}
              >
                View a Sample
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-theme-bg">
        <div className={themeClasses.container}>
          <div className="text-center mb-16">
            <h2 className={`${themeClasses.title.h2} mb-6`}>The Audience</h2>
            <p className={`${themeClasses.text.body} text-lg max-w-3xl mx-auto`}>
              All perks and benefits are featured and promoted on the FHxD Advisor Portal, a
              password-protected platform for participating ICs.
            </p>
          </div>
          <div className={themeClasses.grid.cols3}>
            {advisorNetwork.map((item, index) => (
              <div key={index} className="p-8 border border-theme-border bg-theme-bg-secondary">
                <item.icon className="h-10 w-10 mb-6 text-theme-text-muted" strokeWidth={1} />
                <h3 className={`${themeClasses.title.h5} mb-4`}>{item.title}</h3>
                <p className={`${themeClasses.text.muted}`}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-theme-bg-secondary">
        <div className={themeClasses.container}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h3 className={`${themeClasses.title.h3} mb-6`}>The Four Hundred by Design Audience</h3>
              <p className={`${themeClasses.text.muted} mt-2`}>Primarily US-based</p>
            </div>

            <div className="p-8 border border-theme-border bg-theme-bg">
              <p className={`${themeClasses.text.caption} mb-8 text-center uppercase tracking-widest`}>
                Age Distribution
              </p>
              <div className="space-y-4">
                {ageDistribution.map((age) => (
                  <div key={age.generation} className="flex items-center gap-4">
                    <span className={`${themeClasses.text.body} w-28`}>{age.generation}</span>
                    <div className="flex-1 h-6 bg-theme-bg-secondary overflow-hidden">
                      <div
                        className="h-full bg-theme-text transition-all duration-500"
                        style={{ width: age.width }}
                      />
                    </div>
                    <span className={`${themeClasses.text.body} w-12 text-right`}>{age.percentage}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default PartnerOpportunitiesSections;
